/**
 * fetch wrapper that auto-recovers from a 401 OR 403 by reloading once.
 *
 * Background — Phase A.5 of the user-session migration flips the
 * /dashboard/:locationId/* and /billing/:locationId/* endpoints from
 * shadow-mode (log only) to enforce-mode. After the flip, two failure
 * shapes need recovery:
 *
 *   - 401 (no/invalid cookie) — typically a stale frontend bundle
 *     that pre-dates Phase A.2 and never called /auth/exchange-sso.
 *     A reload picks up the new bundle, mints the cookie, recovers.
 *
 *   - 403 (cookie locationId doesn't match URL locationId) — typically
 *     a user who switched GHL locations mid-session. The cookie still
 *     encodes the old location, the URL has the new one. A reload re-
 *     runs `useGHLIframe` → `requestSSOData` against GHL's parent,
 *     which responds with encrypted blob for the CURRENT location, then
 *     `/auth/exchange-sso` mints a cookie matching the new location.
 *
 * Both flows produce a single `window.location.reload()` for the user.
 *
 * Loop guard — sessionStorage flag survives the reload, so we reload
 * AT MOST once between successful requests. The flag is CLEARED on any
 * 2xx response, which means a power user switching GHL locations many
 * times per session gets one reload per switch (not just the first
 * one). The cleared-on-success behavior is what makes this safe to
 * extend to 403: between any two reloads at least one successful
 * request must have happened, so a permanently broken auth state
 * (e.g. exchange-sso itself failing) still cannot loop.
 *
 * Apply to user-facing fetches whose backend route is covered by the
 * enforce middleware. Don't apply to public endpoints (e.g. /upload,
 * /webhooks/*) — those don't return 401/403 for missing cookies and
 * we don't want to reload on unrelated permission errors.
 *
 * Security note — extending to 403 doesn't aid attackers. A scripted
 * probe (curl/postman) doesn't run JS, so this helper never fires for
 * non-browser clients regardless of status code.
 */

const RELOAD_FLAG = 'oak_auth_reload_attempted';

export async function fetchWithAuthRecovery(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const res = await fetch(input, init);

  // Any successful response clears the flag, "re-arming" the helper so
  // a future 401/403 in the same tab (e.g. on the next GHL location
  // switch) can trigger another reload.
  if (res.ok) {
    sessionStorage.removeItem(RELOAD_FLAG);
    return res;
  }

  if (
    (res.status === 401 || res.status === 403) &&
    !sessionStorage.getItem(RELOAD_FLAG)
  ) {
    // Set the flag BEFORE reloading so it survives the page reset and
    // prevents looping if the new bundle ALSO can't get a valid cookie
    // (e.g. exchange-sso is itself broken).
    sessionStorage.setItem(RELOAD_FLAG, '1');
    window.location.reload();
    // The page is reloading — never resolve. Returning a perpetually
    // pending promise keeps callers' async chains from running on a
    // half-torn-down page.
    return new Promise<Response>(() => {});
  }

  return res;
}
